import { getBasePath } from '../utils/pathUtils';

/**
 * What a signed-in person leaves in this browser (backlog a81cf711): their
 * token, the site they had open (this tab's and the last one picked), and a
 * page to return to after signing in. Signing out forgets all of it, so the
 * next person to sign in here starts from their own sites, never this one's.
 * The theme stays: it belongs to the browser, not the person.
 */
const RETURN_KEY = 'postLoginReturn';
const LOCAL_KEYS = ['token', 'selected_account_id', RETURN_KEY];
const TAB_USER_KEY = 'signedInAs';
const SESSION_KEYS = ['selected_account_id', 'postLoginRedirect', TAB_USER_KEY];
/** How long a return page kept in localStorage still counts. */
const RETURN_MINUTES = 30;

/** In-app path only: leading slash, not protocol-relative or off-site. */
export function safeAppPath(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const path = raw.trim();
  if (!path.startsWith('/') || path.startsWith('//') || /[\s\\]/.test(path)) return null;
  return path;
}

/**
 * The page to come back to after signing in. It used to live only in this
 * tab's sessionStorage, which a phone can lose: open an upload link from a
 * chat app, switch to Mail for the sign-in code, come back to a restored tab,
 * and sign-in landed on Today (backlog 016a05f9). So it also rides in the
 * sign-in address (/login?next=…) and, for a while, in localStorage.
 */
export function rememberReturnPath(raw: unknown) {
  const path = safeAppPath(raw);
  if (!path || path === '/' || path.startsWith('/login')) return;
  try {
    sessionStorage.setItem('postLoginRedirect', path);
  } catch {
    // Storage blocked: the address still carries it.
  }
  try {
    localStorage.setItem(RETURN_KEY, JSON.stringify({ path, at: Date.now() }));
  } catch {
    // As above.
  }
}

/** /login, carrying the page to return to. */
export function loginPathFor(raw: unknown): string {
  const path = safeAppPath(raw);
  return path && path !== '/' && !path.startsWith('/login')
    ? `/login?next=${encodeURIComponent(path)}`
    : '/login';
}

/** /signup, carrying the page to return to once the account is made. */
export function signupPathFor(raw: unknown): string {
  return loginPathFor(raw).replace(/^\/login/, '/signup');
}

/**
 * The page this tab is on, as an app path (without the /account base), so a
 * session that runs out mid-page can send sign-in back to it.
 */
export function currentAppPath(): string {
  const base = getBasePath();
  let path = window.location.pathname;
  if (path === base || path.startsWith(`${base}/`)) path = path.slice(base.length) || '/';
  return path + window.location.search;
}

/**
 * Where sign-in goes next, once: the address's ?next=, else this tab's
 * sessionStorage, else a recent localStorage copy. All three are cleared.
 */
export function takeReturnPath(search: string = ''): string | null {
  let fromUrl: string | null = null;
  try {
    fromUrl = new URLSearchParams(search).get('next');
  } catch {
    fromUrl = null;
  }
  let fromSession: string | null = null;
  let fromLocal: string | null = null;
  try {
    fromSession = sessionStorage.getItem('postLoginRedirect');
    sessionStorage.removeItem('postLoginRedirect');
  } catch {
    // Storage blocked.
  }
  try {
    const kept = JSON.parse(localStorage.getItem(RETURN_KEY) || 'null');
    localStorage.removeItem(RETURN_KEY);
    if (kept && Date.now() - Number(kept.at) < RETURN_MINUTES * 60000) fromLocal = kept.path;
  } catch {
    // Storage blocked, or not ours.
  }
  for (const raw of [fromUrl, fromSession, fromLocal]) {
    const path = safeAppPath(raw);
    if (path && !path.startsWith('/login')) return path;
  }
  return null;
}

/**
 * Who this tab was last signed in as. The session cookie is the browser's, so
 * another tab can swap or end it; this tab's own record is what can tell.
 */
export function tabUser(): string | null {
  try {
    return sessionStorage.getItem(TAB_USER_KEY);
  } catch {
    return null;
  }
}

export function rememberTabUser(id: string | null) {
  try {
    if (id) sessionStorage.setItem(TAB_USER_KEY, id);
    else sessionStorage.removeItem(TAB_USER_KEY);
  } catch {
    // Storage blocked: the sign-in page just says less.
  }
}

export function forgetSignedInUser() {
  for (const key of LOCAL_KEYS) {
    try {
      localStorage.removeItem(key);
    } catch {
      // Storage blocked: nothing was kept there either.
    }
  }
  for (const key of SESSION_KEYS) {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // As above.
    }
  }
}
